/**
 * schemaOrg.js — JSON-LD builders for Winhouse Tutors.
 *
 * ALL builders produce schema.org objects that MUST match visible page
 * content. Do not fabricate reviews, ratings, credentials, addresses or
 * awards.
 */

const SITE_URL = 'https://winhousetutors.academy';
const ORG_NAME = 'Winhouse Tutors';
const ORG_LEGAL_NAME = 'Winhouse Tutors';
const ORG_LOGO = `${SITE_URL}/winhouse-logo-final-optimized.png`;

// Organization + Educational Organization (dual-typed).
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['Organization', 'EducationalOrganization'],
  '@id': `${SITE_URL}#organization`,
  name: ORG_NAME,
  legalName: ORG_LEGAL_NAME,
  alternateName: ['Winhouse', 'Winhouse Academy', 'Winhouse Consultancy'],
  url: SITE_URL,
  logo: ORG_LOGO,
  image: `${SITE_URL}/og-image.png?v=2`,
  slogan: 'Global online tutoring, test preparation and international admissions',
  description:
    'Winhouse Tutors is a global online education platform delivering one-to-one tutoring, English-proficiency test preparation (IELTS · PTE · TOEFL), university-entrance test coaching (SAT · GMAT · GRE), Australian VCE and Selective Entry preparation, and study-abroad and university admissions support.',
  areaServed: [
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'Country', name: 'New Zealand' },
    { '@type': 'Country', name: 'Kenya' },
    { '@type': 'Country', name: 'Nigeria' },
    { '@type': 'Country', name: 'Ghana' },
    { '@type': 'Country', name: 'South Africa' },
    { '@type': 'Country', name: 'India' },
    { '@type': 'Country', name: 'Pakistan' },
    { '@type': 'Country', name: 'Bangladesh' },
    { '@type': 'Country', name: 'Nepal' },
    { '@type': 'Country', name: 'Sri Lanka' },
    { '@type': 'Country', name: 'South Korea' },
    { '@type': 'Country', name: 'Japan' },
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'Country', name: 'United States' },
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'Country', name: 'Saudi Arabia' },
    { '@type': 'Country', name: 'Qatar' },
  ],
  knowsLanguage: ['en'],
  sameAs: [],
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  url: SITE_URL,
  name: ORG_NAME,
  publisher: { '@id': `${SITE_URL}#organization` },
  inLanguage: 'en',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/tutors?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.path.startsWith('http') ? item.path : `${SITE_URL}${item.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const articleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  '@id': `${SITE_URL}/blog/${article.slug}#article`,
  headline: article.title,
  description: article.description,
  articleSection: article.category,
  keywords: (article.keywords || []).join(', '),
  wordCount: article.wordCount,
  datePublished: article.publishedAt,
  dateModified: article.updatedAt || article.publishedAt,
  inLanguage: 'en',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${article.slug}` },
  author: {
    '@type': 'Person',
    name: article.author?.name || 'Winhouse Editorial Team',
    url: `${SITE_URL}/blog?author=${encodeURIComponent(article.author?.slug || 'winhouse-editorial')}`,
  },
  publisher: { '@id': `${SITE_URL}#organization` },
  image: article.image ? `${SITE_URL}${article.image}` : `${SITE_URL}/og-image.png?v=2`,
});

export const courseSchema = (course) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: course.name,
  description: course.description,
  provider: { '@id': `${SITE_URL}#organization` },
  educationalCredentialAwarded: course.credential,
  hasCourseInstance: {
    '@type': 'CourseInstance',
    courseMode: 'Online',
    courseWorkload: course.workload,
  },
});

export const webPageSchema = ({ title, description, path, isPartOf = true }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}${path}#webpage`,
  url: `${SITE_URL}${path}`,
  name: title,
  description,
  inLanguage: 'en',
  ...(isPartOf ? { isPartOf: { '@id': `${SITE_URL}#website` } } : {}),
  publisher: { '@id': `${SITE_URL}#organization` },
});

/** Combine multiple JSON-LD objects into an array — helmet renders each. */
export const combineSchemas = (...schemas) => schemas.filter(Boolean);
